import React, { useState, useEffect, useRef } from "react";
import gsap from "gsap";
import "./App.css";

import Navbar from "./components/Navbar";
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const hydratonMessages = [
  "Hey! I’m Hydraton. Let’s check your hydration!",
  "Cool! What’s your name, buddy?",
  "Tell me when you last drank water and how much!",
  "Place your hand on the sensor. I’ll read it.",
  "Here are some facts while I crunch the numbers...",
  "Done! Here's your hydration status 🧪",
];

const hydrationQuotes = {
  "Severely Dehydrated":
    "Even your body knows it’s time for a refill. Go hydrate now!",
  Optimal: "You’re doing great. Stay consistent, stay sharp.",
  "Severely Hydrated":
    "Too much of a good thing can slow you down. Balance is key!",
};

const hydrationFacts = [
  "💧 Water makes up about 60% of your body weight.",
  "💧 Hydration helps regulate your body temperature.",
  "💧 Drinking enough water can improve mood and focus.",
  "💧 Thirst is a late sign of dehydration—don’t wait for it.",
  "💧 Proper hydration supports healthy skin and elasticity.",
  "💧 Drinking water before meals can aid in digestion.",
  "💧 Muscles are around 75% water—hydration boosts performance.",
  "💧 Water helps cushion joints and prevent injuries.",
  "💧 Hydrated kidneys function better, reducing stone risk.",
  "💧 Dehydration can mimic hunger, leading to overeating.",
];

const time_of_animation = 5000;

export default function App() {
  const [step, setStep] = useState(0);
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    lastDrink: "",
    amount: "",
  });
  const [hydrationStatus, setHydrationStatus] = useState("");
  const [voiceOn, setVoiceOn] = useState(true);
  const containerRef = useRef();
  const message = hydratonMessages[step] || "";

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(
        containerRef.current,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power2.out" }
      );
    }
  }, [step]);

  useEffect(() => {
    gsap.utils.toArray(".rain-droplet").forEach((drop) => {
      gsap.fromTo(
        drop,
        {
          y: -100,
          x: Math.random() * window.innerWidth,
          opacity: 0.2,
        },
        {
          y: window.innerHeight,
          duration: Math.random() * 2 + 2,
          delay: Math.random() * 5,
          repeat: -1,
          opacity: 0.7,
          ease: "power1.inOut",
        }
      );
    });
  }, []);

  useEffect(() => {
    if (step === 5) {
      (async () => {
        await sleep(time_of_animation * 2);
        setStep(0);
      })();
    }
  }, [step]);

  useEffect(() => {
    if (step === 4) {
      gsap.fromTo(
        ".fact-item",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          stagger: 1,
          duration: 0.5,
          ease: "power2.out",
        }
      );

      const extraRainContainer = document.createElement("div");
      extraRainContainer.className = "droplet-background heavy-rain";
      document.body.appendChild(extraRainContainer);

      const count = 80;
      for (let i = 0; i < count; i++) {
        const drop = document.createElement("div");
        drop.className = "rain-droplet";
        drop.style.left = `${Math.random() * 100}vw`;
        drop.style.animationDuration = `${1.5 + Math.random()}s`;
        drop.style.opacity = Math.random() * 0.7 + 0.3;
        extraRainContainer.appendChild(drop);
      }

      const timeout = setTimeout(() => {
        document.body.removeChild(extraRainContainer);
        setStep(5);
      }, time_of_animation);

      return () => {
        clearTimeout(timeout);
        if (extraRainContainer && document.body.contains(extraRainContainer)) {
          document.body.removeChild(extraRainContainer);
        }
      };
    }
  }, [step]);

  useEffect(() => {
    if (!voiceOn || !message || typeof message !== "string") return;
    const synth = window.speechSynthesis;
    synth.cancel();
    const utter = new SpeechSynthesisUtterance(message);
    utter.rate = 1.1;
    utter.pitch = 0.9;
    const voices = synth.getVoices();
    const preferredVoice = voices.find((v) => v.name === "Catherine");
    if (preferredVoice) {
      utter.voice = preferredVoice;
    }
    synth.speak(utter);
  }, [message, voiceOn]);

  useEffect(() => {
    if (step === 4) {
      gsap.fromTo(
        ".fact-item",
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          stagger: 1,
          duration: 0.5,
          ease: "power2.out",
        }
      );
      setTimeout(() => setStep(5), time_of_animation);
    }
  }, [step]);
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(true);

  useEffect(() => {
    const dropletContainer = document.createElement("div");
    dropletContainer.className = "droplet-background";
    document.body.appendChild(dropletContainer);
    const count = 60;
    for (let i = 0; i < count; i++) {
      const drop = document.createElement("div");
      drop.className = "rain-droplet";
      drop.style.left = `${Math.random() * 100}vw`;
      drop.style.animationDelay = `${Math.random() * 3}s`;
      drop.style.animationDuration = `${2 + Math.random() * 2}s`;
      dropletContainer.appendChild(drop);
    }
    return () => {
      document.body.removeChild(dropletContainer);
    };
  }, []);

  const handleNext = () => setStep((prev) => prev + 1);
  const handleNextwithpause = async () => {
    handleNext();
    await sleep(time_of_animation);
  };

  const simulateSensorData = async () => {
    handleNextwithpause();
    try {
      const response = await fetch(
        "https://lidsense.com/backend/public/api/calculate/hydration", //api
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            lastDrink: user.lastDrink,
            amount: user.amount,
            weight: "70",
            height: "172",
            age: "18",
            gender: "male",
            status: "Optimal",
          }),
        }
      );
      console.log("api hit success");

      if (!response.ok) {
        throw new Error("API request failed");
      }

      const data = await response.json();
      console.log(data);
      setHydrationStatus(data.data.hydration_status);
      console.log(data.data.hydration_status);
    } catch (error) {
      console.error("Sensor data error:", error);
      alert("There was an issue fetching hydration status. Please try again.");
    }
  };

  return (
    <>
      {" "}
      <Navbar voiceOn={voiceOn} toggleVoice={() => setVoiceOn(!voiceOn)} />
      <div className="app">
        <div className="droplet-background">
          {Array.from({ length: 25 }).map((_, i) => (
            <div key={i} className="rain-droplet" />
          ))}
        </div>

        {/* 👇 Fixed mascot outside the screen */}
        <div className="fixed-mascot-wrapper">
          <img
            src="/hydraton.png"
            alt="Hydraton Mascot"
            className="fixed-mascot"
          />
        </div>

        <div className="screen" ref={containerRef}>
          <div className="bubble-box">
            <div className="hydraton-bubble">
              {message}
              <span className="bubble-glow" />
            </div>
          </div>
          {step === 0 && (
            <>
              <h1>Your Hydration Journey</h1>
              <button onClick={handleNext}>Start</button>
            </>
          )}
          {step === 1 && (
            <>
              <h2>What’s your name?</h2>
              <input
                placeholder="First Name"
                onChange={(e) =>
                  setUser({ ...user, firstName: e.target.value })
                }
              />
              <input
                placeholder="Last Name"
                onChange={(e) => setUser({ ...user, lastName: e.target.value })}
              />
              <button onClick={handleNext}>Next</button>
            </>
          )}
          {step === 2 && (
            <>
              <h2>Water Intake</h2>
              <input
                placeholder="Last time you drank water?"
                onChange={(e) =>
                  setUser({ ...user, lastDrink: e.target.value })
                }
              />
              <input
                placeholder="Amount today (ml)?"
                onChange={(e) => setUser({ ...user, amount: e.target.value })}
              />
              <button onClick={handleNext}>Next</button>
            </>
          )}
          {step === 3 && (
            <div className="hydration-facts-container">
              {showPrivacyPopup ? (
                <div className="privacy-popup">
                  <h3>Privacy Notice</h3>
                  <p>
                    By continuing, you agree to share hydration sensor data for
                    analysis and visualization during this demo. No personal
                    info is stored.
                  </p>
                  <a
                    href="/privacy-policy.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Full Policy
                  </a>
                  <br />
                  <button onClick={() => setShowPrivacyPopup(false)}>
                    Accept
                  </button>
                </div>
              ) : (
                <>
                  <h2>Place your hand on the sensor</h2>
                  <button onClick={simulateSensorData}>
                    Confirm Hand is Placed
                  </button>
                </>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="hydration-facts-container">
              <div className="hydration-facts">
                {hydrationFacts
                  .sort(() => 0.5 - Math.random()) // Shuffle array
                  .slice(0, 2) // Pick first 2
                  .map((fact, idx) => (
                    <div className="fact-item" key={idx}>
                      {fact}
                    </div>
                  ))}
              </div>
            </div>
          )}
          {step === 5 && (
            <>
              <h2>Hydration Status: {hydrationStatus}</h2>
              <p>{hydrationQuotes[hydrationStatus]}</p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
